import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactECharts from 'echarts-for-react';
import CustomDropdown from 'components/Dropdown';
import LoaderComponent from 'components/Loader';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { InfoTooltip, TooltipLabel } from 'components/Widgets/InfoTooltip';

interface CapacityData {
    month: string;
    CGF: number;
    PBNA: number;
    PFNA: number;
    EUROPE: number;
    LATAM: number;
    AMESA_APAC: number;
}

type CapacityDataNumericKey = Exclude<keyof CapacityData, 'month'>;

interface CapacityGrowthChartProps {
    data?: CapacityData[];
    height?: string;
    onSegmentClick?: (sector: string, month: string) => void;
    selectedSegment?: string;
    isLoading?: boolean;
    onTimeFilterChange?: (timePeriod: string) => void;
    hasTooltip?: boolean;
    tooltipLabel?: TooltipLabel;
}

const CapacityGrowthChart: React.FC<CapacityGrowthChartProps> = ({
    data = [],
    height = 'calc(100% - 32px)',
    onSegmentClick,
    selectedSegment = '',
    isLoading = false,
    onTimeFilterChange,
    hasTooltip,
    tooltipLabel
}) => {
    const theme = useTheme();
    const colors = theme.palette;
    const [timeFilter, setTimeFilter] = useState('Monthly');

    // Memoize chart data to prevent unnecessary re-renders
    const chartData = useMemo(() => {
        return data && data.length > 0 ? data : [];
    }, [data]);

    const handleTimeFilterChange = useCallback((event: any) => {
        const newTimeFilter = event.target.value;
        setTimeFilter(newTimeFilter);

        const chartTimePeriodMap: { [key: string]: string } = {
            'Monthly': 'Monthly',
            'Weekly': 'Weekly'
        };

        if (onTimeFilterChange) {
            const chartTimePeriod = chartTimePeriodMap[newTimeFilter] || 'Monthly';
            onTimeFilterChange(chartTimePeriod);
        }
    }, [onTimeFilterChange]);

    const handleChartClick = useCallback((params: any) => {
        if (onSegmentClick && params.componentType === 'series') {
            const month = params.name;
            let sector = params.seriesName;

            if (sector === 'AMESA/APAC') {
                sector = 'AMESA_APAC';
            }

            onSegmentClick(sector, month);
        }
    }, [onSegmentClick]);

    // Memoize filtered data
    const filteredData = useMemo(() => {
        switch (timeFilter) {
            case 'Weekly':
                return chartData.slice(-3);
            case 'Monthly':
            default:
                return chartData;
        }
    }, [chartData, timeFilter]);

    // Define series configuration with colors
    const seriesConfig: Array<{ name: string; dataKey: CapacityDataNumericKey; color: string }> = [
        { name: 'CGF', dataKey: 'CGF', color: '#fd783b' },
        { name: 'PBNA', dataKey: 'PBNA', color: '#39a0ff' },
        { name: 'PFNA', dataKey: 'PFNA', color: '#00a556' },
        { name: 'EUROPE', dataKey: 'EUROPE', color: '#932020' },
        { name: 'LATAM', dataKey: 'LATAM', color: '#fe2626' },
        { name: 'AMESA/APAC', dataKey: 'AMESA_APAC', color: '#ffc231' },
    ];

    // Memoize chart option to prevent unnecessary re-renders
    const customChartOption: any = useMemo(() => ({
        backgroundColor: colors.widget?.background?.chart,
        tooltip: {
            trigger: 'axis',
            backgroundColor: colors.widget?.chart?.tooltipBg,
            borderColor: colors.widget?.chart?.legendTextColor,
            borderWidth: 1,
            textStyle: {
                color: colors.widget?.chart?.tooltipText,
                fontSize: 12
            },
            axisPointer: {
                type: 'shadow',
                shadowStyle: {
                    color: colors.widget?.chart?.emptyCircleFill
                }
            },
            formatter: function (params: any) {
                let tooltipContent = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].name}</div>`;
                let totalCapacities = 0;

                params.forEach((param: any) => {
                    if (param.value > 0) {
                        totalCapacities += param.value;
                        tooltipContent += `
                            <div style="display: flex; align-items: center; margin-bottom: 4px;">
                                <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; margin-right: 8px; border-radius: 2px;"></span>
                                <span style="margin-right: 8px;">${param.seriesName}:</span>
                                <span style="font-weight: bold;">${param.value}</span>
                            </div>
                        `;
                    }
                });

                tooltipContent += `<div style="border-top: 1px solid ${colors.widget?.chart?.legendTextColor}; padding-top: 6px; margin-top: 6px;">Total Capacities: ${totalCapacities}</div>`;

                return tooltipContent;
            }
        },
        legend: {
            show: true,
            type: 'scroll',
            orient: 'horizontal',
            left: '20px',
            bottom: '20px',
            itemWidth: 14,
            itemHeight: 14,
            itemGap: 15,
            textStyle: {
                color: colors.widget?.chart?.legendTextColor,
                fontSize: 11
            },
            pageIconColor: colors.widget?.chart?.legendActiveUpDownIconColor,
            pageIconInactiveColor: colors.widget?.chart?.legendInactiveUpDownIconColor,
            pageTextStyle: {
                color: colors.widget?.chart?.legendTextColor,
                fontSize: 10
            },
            data: seriesConfig.map(s => s.name)
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '25%',
            top: '10%',
            containLabel: true
        },
        xAxis: {
            type: 'category' as const,
            data: filteredData.map(item => item.month),
            axisLabel: {
                color: colors.widget?.chart?.labelColorSecondary,
                fontSize: 11,
                interval: 0,
                rotate: 0
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: colors.widget?.chart?.legendTextColor
                }
            },
            axisTick: {
                show: false
            },
            splitLine: {
                show: false
            }
        },
        yAxis: {
            type: 'value' as const,
            axisLabel: {
                color: colors.widget?.chart?.labelColorSecondary,
                fontSize: 11,
                formatter: function (value: number) {
                    if (value >= 1000000) {
                        return (value / 1000000).toFixed(1) + 'M';
                    } else if (value >= 1000) {
                        return (value / 1000).toFixed(0) + 'K';
                    }
                    return value.toString();
                }
            },
            axisLine: {
                show: false
            },
            axisTick: {
                show: false
            },
            splitLine: {
                show: true,
                lineStyle: {
                    type: 'dashed',
                    color: colors.widget?.chart?.emptyCircleFill,
                    width: 1
                }
            }
        },
        series: seriesConfig.map((series, index: number) => ({
            name: series.name,
            type: 'bar' as const,
            stack: 'total',
            data: filteredData.map((item) => item[series.dataKey] || 0),
            itemStyle: {
                color: series.color
            },
            emphasis: {
                focus: 'series',
                itemStyle: {
                    shadowBlur: 10,
                    shadowColor: `rgba(${Number.parseInt(series.color.slice(1, 3), 16)}, ${Number.parseInt(series.color.slice(3, 5), 16)}, ${Number.parseInt(series.color.slice(5, 7), 16)}, 0.5)`
                }
            },
            barWidth: '80%',
            // Add label only for the last series (top of the stack)
            label: index === seriesConfig.length - 1 ? {
                show: true,
                position: 'top',
                formatter: function (params: any) {
                    // Calculate total for this bar by summing all sectors
                    let total = 0;
                    const dataIndex = params.dataIndex;
                    const currentItem = filteredData[dataIndex];

                    // Sum all sector values for this period
                    for (const s of seriesConfig) {
                        total += currentItem[s.dataKey] || 0;
                    }

                    // Format the number
                    if (total >= 1000000) {
                        return (total / 1000000).toFixed(1) + 'M';
                    } else if (total >= 1000) {
                        return (total / 1000).toFixed(1) + 'k';
                    }
                    return total.toString();
                },
                color: colors.widget?.chart?.labelColorSecondary,
                fontSize: 11,
                fontWeight: 'bold'
            } : undefined
        }))
    }), [filteredData, colors]);

    const renderChartContent = () => {
        if (isLoading) {
            return <LoaderComponent />;
        }

        if (chartData.length === 0) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography sx={{ color: colors.widget?.text?.noChange, fontSize: '16px', fontFamily: 'Montserrat' }}>
                        No data available
                    </Typography>
                </Box>
            );
        }

        return (
            <ReactECharts
                option={customChartOption}
                style={{ width: '100%', height: '100%' }}
                opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
                notMerge={false}
                lazyUpdate={false}
                onEvents={{
                    'click': handleChartClick
                }}
            />
        );
    };

    return (
        <Stack
            minHeight="100px"
            p={2}
            bgcolor={colors.widget?.background?.chart}
            fontFamily="Montserrat"
            borderRadius="5px"
            flexGrow={1}
            height={height}
        >
            <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" pb={1}>
                <Stack direction="row" alignItems="center">
                    <Typography variant="widgetTitle" sx={{ color: colors.widget?.text?.title }}>Capacity Growth</Typography>
                    {hasTooltip && tooltipLabel && <InfoTooltip label={tooltipLabel} />}
                </Stack>
                <Box display='flex' gap={1}>
                    <CustomDropdown
                        handleChange={handleTimeFilterChange}
                        value={timeFilter}
                        options={[
                            { label: 'Monthly', value: 'Monthly' },
                            { label: 'Weekly', value: 'Weekly' }
                        ]}
                        dropdownIcon={KeyboardArrowDownIcon}
                        selectedTextColor={colors.DropdownV2?.dropdownIconDefault}
                        textColor={colors.DropdownV2?.menuItemText}
                        iconColor={colors.DropdownV2?.dropdownIconDefault}
                        bgColor={colors.DropdownV2?.menuItemBackground}
                        borderColor="transparent"
                        hoverBgColor={colors.DropdownV2?.menuItemHover}
                        hoverTextColor={colors.DropdownV2?.menuItemHoverText}
                        selectedBgColor={colors.DropdownV2?.menuItemSelectedBg}
                        boxProps={{
                            sx: {
                                backgroundColor: 'transparent !important',
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'transparent !important'
                                },
                                '& .MuiSelect-select': {
                                    fontFamily: 'Montserrat',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    padding: '0 26px 0 0 !important',
                                    minHeight: '28px !important',
                                    display: 'flex',
                                    alignItems: 'center'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none !important'
                                },
                                '& .MuiSelect-icon': {
                                    color: `${colors.DropdownV2?.dropdownIconDefault} !important`,
                                    right: 0
                                }
                            }
                        }}
                    />
                </Box>
            </Stack>
            <Stack height="280px" flexGrow={1} justifyContent="center">
                {renderChartContent()}
            </Stack>
        </Stack>
    );
};

// Memoize the component to prevent unnecessary re-renders when parent re-renders
export default React.memo(CapacityGrowthChart, (prevProps, nextProps) => {
    // Only re-render if these props actually change
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    if (prevProps.selectedSegment !== nextProps.selectedSegment) return false;
    if (prevProps.height !== nextProps.height) return false;

    // Deep comparison for data array
    const prevData = prevProps.data || [];
    const nextData = nextProps.data || [];
    if (prevData.length !== nextData.length) return false;

    for (let i = 0; i < prevData.length; i++) {
        const prevItem = prevData[i];
        const nextItem = nextData[i];
        if (prevItem.month !== nextItem.month ||
            prevItem.CGF !== nextItem.CGF ||
            prevItem.PBNA !== nextItem.PBNA ||
            prevItem.PFNA !== nextItem.PFNA ||
            prevItem.EUROPE !== nextItem.EUROPE ||
            prevItem.LATAM !== nextItem.LATAM ||
            prevItem.AMESA_APAC !== nextItem.AMESA_APAC) {
            return false;
        }
    }

    return true;
});